import { Col, Form, Row } from 'antd';
import AddItemButton from '@/components/AddItemButton.jsx';
import CommonFooter from '@/components/CommonFooter';
import CustomInput from '@/components/CustomInput';
import CustomTitle from '@/components/CustomTitle';
import NotificationBanner from '@/components/NotificationBanner.jsx';
import infoIcon from '@/assets/images/info-icon.svg';
import CustomDeleteModal from '../../Modals/CustomDeleteModal.jsx';
import {
  CredentialEditorForm,
  CredentialList,
  ProviderDetailsHeader,
} from './components';
import { useSensorProviderDetails } from './hooks/useSensorProviderDetails';
import type { SensorProviderDetailsProps } from './types';
import './sensors.css';

const SENSOR_DESCRIPTION_MAX_LENGTH = 300;

const SensorProviderDetails: React.FC<SensorProviderDetailsProps> = ({
  credentials,
  providerData,
  onClose,
  edit,
  setModalState,
  getSensorImagePath,
  drawerCredentialView,
  setDrawerCredentialView,
}) => {
  const controller = useSensorProviderDetails({
    credentials,
    drawerCredentialView,
    onClose,
    providerData,
    setDrawerCredentialView,
    setModalState,
  });

  if (controller.credentialView === 'credential') {
    return (
      <CredentialEditorForm
        credentialForm={controller.credentialForm}
        credentialSets={controller.credentialSets}
        editingCredentialIndex={controller.editingCredentialIndex}
        instructions={controller.instructions}
        onCancel={controller.cancelCredentialEdit}
        onFinish={controller.onFinishCredential}
        requiredInputs={controller.requiredInputs}
      />
    );
  }

  return (
    <Form
      form={controller.form}
      onFinish={controller.onFinish}
      layout='vertical'
      className='sensor-provider-details-form'
    >
      <div className='sensor-details-sticky-header'>
        <ProviderDetailsHeader
          getSensorImagePath={getSensorImagePath}
          provider={providerData}
          edit={edit}
          handleMenuClick={controller.handleMenuClick}
        />
      </div>

      {controller.notification && (
        <NotificationBanner
          icon={infoIcon}
          message='A Zoomlogi representative will reach out to you to complete integration'
        />
      )}

      <div className='custom-scrollbar sensor-details-scroll'>
        <Row gutter={[16, 14]}>
          <Col span={24}>
            <Form.Item
              className='custom-input form-input-style'
              rules={[
                { required: false, message: 'Please enter description!' },
                {
                  type: 'string',
                  min: 3,
                  message: 'Description must be at least 3 characters long!',
                },
                {
                  max: SENSOR_DESCRIPTION_MAX_LENGTH,
                  message: `Description cannot exceed ${SENSOR_DESCRIPTION_MAX_LENGTH} characters`,
                },
              ]}
              label='Description'
              name='description'
            >
              <CustomInput
                placeholder='Enter sensor description'
                bgColor='var(--light-gray)'
                height='36px'
                textColor='var(--text-primary)'
                borderRadius='8px'
                border='none'
                placeholderColor='var(--text-secondary)'
                maxLength={SENSOR_DESCRIPTION_MAX_LENGTH}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <CustomTitle
              text='Credentials'
              fontSize={15}
              fontWeight='600'
              color='var(--text-primary)'
              marginBottom={4}
            />
            <div className='sensor-credentials-help'>
              If no credentials are selected, the primary credentials will be
              used by default
            </div>
          </Col>

          <Col span={24}>
            <CredentialList
              credentialSets={controller.credentialSets}
              sensors={controller.sensors}
              onDragEnd={controller.handleCredentialDragEnd}
              onEditCredential={controller.openEditCredential}
              onDeleteCredential={controller.deleteCredential}
            />
          </Col>

          <Col span={24}>
            <AddItemButton
              gap='24px'
              width='28px'
              height='28px'
              marginTop='0px'
              text='Add credentials'
              fontSize='12px'
              fontWeight='600'
              onClick={controller.openAddCredential}
            />
          </Col>
        </Row>

        <CustomDeleteModal
          visible={controller.deleteModalVisible}
          onClose={() => controller.setDeleteModalVisible(false)}
          onConfirm={() => {}}
          title='Remove Contact'
          message='Removing this contact will prevent selecting it for future use.'
          cancelText='Cancel'
          confirmText='Remove'
          confirmColor='var(--danger)'
        />
      </div>

      <div className='sensor-drawer-footer'>
        <CommonFooter
          text='Save'
          onSave={() => controller.form.submit()}
          handleCancel={onClose}
        />
      </div>
    </Form>
  );
};

export default SensorProviderDetails;
